import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle, Clock, Download, UserX } from 'lucide-react';
import { toast } from 'sonner';

import { facultyExamApi } from '../../api/examApi';
import { checkPdfFile } from '../../lib/uploadLimits';
import UploadHint from '../UploadHint';
import { parseApiError } from '../../lib/apiErrors';

const STATE_CFG = {
  pending: { label: 'Pending', cls: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300', Icon: Clock },
  present: { label: 'Present', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', Icon: CheckCircle },
  absent: { label: 'Absent', cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300', Icon: UserX },
};

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function RosterRow({ examId, row, onChanged }) {
  const [marks, setMarks] = useState(row.marks ?? '');
  const [feedback, setFeedback] = useState(row.feedback ?? '');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const cfg = STATE_CFG[row.state];
  const saved = row.state === 'present';

  const onFile = (e) => {
    const f = e.target.files?.[0] || null;
    const err = checkPdfFile(f, 'Corrected sheet');
    if (err) {
      toast.error(err);
      e.target.value = '';
      return;
    }
    setFile(f);
  };

  const save = async () => {
    if (marks === '' || Number.isNaN(Number(marks))) return toast.error('Enter the marks');
    if (!file && !row.has_corrected_paper) return toast.error('Attach the corrected-sheet PDF');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('marks', String(marks));
      if (feedback) fd.append('feedback', feedback);
      if (file) fd.append('file', file);
      await facultyExamApi.saveOfflineEvaluation(examId, row.assignment_id, fd);
      toast.success(saved ? 'Updated' : 'Saved - student marked present');
      setFile(null);
      setOpen(false);
      onChanged();
    } catch (err) {
      toast.error(parseApiError(err, 'Save failed').message);
    } finally {
      setBusy(false);
    }
  };

  const toggleAbsent = async () => {
    setBusy(true);
    try {
      if (row.state === 'absent') await facultyExamApi.undoOfflineAbsent(examId, row.assignment_id);
      else await facultyExamApi.markOfflineAbsent(examId, row.assignment_id);
      onChanged();
    } catch (err) {
      toast.error(parseApiError(err, 'Update failed').message);
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    try {
      const res = await facultyExamApi.downloadCorrectedPaper(row.attempt_id);
      saveBlob(res.data, `corrected_${row.student_name}.pdf`);
    } catch {
      toast.error('Download failed');
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700" data-testid={`offline-row-${row.student_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-slate-900 dark:text-white">{row.student_name}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {row.student_class ? `Class ${row.student_class}` : 'Class not set'}
            {saved && ` · ${row.marks}/${row.total_marks}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${cfg.cls}`}>
            <cfg.Icon size={12} /> {cfg.label}
          </span>
          {saved && row.has_corrected_paper && (
            <button
              type="button"
              onClick={download}
              className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200"
            >
              <Download size={12} /> Corrected sheet
            </button>
          )}
          {row.state !== 'absent' && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-light"
            >
              {saved ? 'Edit / replace' : 'Mark present'}
            </button>
          )}
          {!saved && (
            <button
              type="button"
              onClick={toggleAbsent}
              disabled={busy}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              {row.state === 'absent' ? 'Undo absent' : 'Mark absent'}
            </button>
          )}
        </div>
      </div>

      {open && row.state !== 'absent' && (
        <div className="mt-4 grid gap-3 border-t border-slate-200 pt-4 dark:border-slate-700 md:grid-cols-[8rem_1fr]">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300" htmlFor={`m-${row.assignment_id}`}>
              Marks (out of {row.total_marks})
            </label>
            <input
              id={`m-${row.assignment_id}`}
              type="number"
              min="0"
              max={row.total_marks}
              step="any"
              value={marks}
              onChange={(e) => setMarks(e.target.value)}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300" htmlFor={`f-${row.assignment_id}`}>
              Feedback (optional)
            </label>
            <textarea
              id={`f-${row.assignment_id}`}
              rows={2}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
            />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">
              Corrected sheet {saved && '(leave empty to keep the current file)'}
            </label>
            <input
              type="file"
              accept=".pdf,application/pdf"
              onChange={onFile}
              className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-md file:border-0 file:bg-slate-100 file:px-4 file:py-2 file:text-slate-700 dark:text-slate-300 dark:file:bg-slate-700 dark:file:text-slate-200"
            />
            <p className="mt-1"><UploadHint /></p>
          </div>
          <div className="md:col-span-2">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OfflineEvaluationPanel({ examId, onChanged }) {
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await facultyExamApi.getOfflineRoster(examId);
      setRows(res.data);
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to load roster').message);
      setRows([]);
    }
  }, [examId]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = () => {
    load();
    if (onChanged) onChanged();
  };

  const counts = (rows || []).reduce((acc, r) => ({ ...acc, [r.state]: (acc[r.state] || 0) + 1 }), {});

  return (
    <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
      <div className="border-b border-slate-200 p-5 dark:border-slate-700">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Paper test results</h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Saving a corrected sheet with marks marks the student present. Students see the sheet, score and
          feedback together, only after you save.
        </p>
        {rows && rows.length > 0 && (
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            {counts.present || 0} present · {counts.absent || 0} absent · {counts.pending || 0} pending
          </p>
        )}
      </div>
      <div className="space-y-3 p-5">
        {rows === null ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Assign students above to start recording results.</p>
        ) : (
          rows.map((r) => <RosterRow key={r.assignment_id} examId={examId} row={r} onChanged={refresh} />)
        )}
      </div>
    </div>
  );
}
